import css from './dashboard.module.scss';

import { memo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useMe } from 'com/App/WithMe';
import { useDeckEditor } from 'com/DeckEditor';
import { useTestEditor } from 'com/TestEditor';
import { useUserEditor } from 'com/UserEditor';
import { apiGetBackup, apiLogout, getBackupFileName } from 'core/api';
import { saveAsFile } from 'core/helpers/browser';
import {
  getAdminUrl,
  getDeckUrl,
  getFavIconUrl,
  getTestQuestionsUrl,
} from 'core/paths';
import Button from 'core/ui/Button';
import { Menu, MenuItem, MenuHr } from 'core/ui/Menu';
import { useMessageModal } from 'core/ui/MessageModal';
import ICONS, { Icon } from 'core/ui/icons';

import NSFWMenuItem from '../NSFW/NSFWMenuItem';

type Props = {
  dashboard: TDashboard | null;
};

const AppHeader = ({ dashboard }: Props) => {
  const { createDeck } = useDeckEditor();
  const { createTest } = useTestEditor();
  const { editUser } = useUserEditor();
  const { showError } = useMessageModal();

  const me = useMe();

  const navigate = useNavigate();

  return (
    <div className={css.appHeader}>
      <img className={css.appIcon} src={getFavIconUrl()} />
      <InfoPanel dashboard={dashboard} />
      <Menu
        anchor={({ setRef, toggle }) => (
          <Button
            onClick={toggle}
            onRef={setRef}
            theme="flat"
            children={ICONS.Menu}
          />
        )}
      >
        <NSFWMenuItem />
        <BackupMenuItem />
        <MenuItem
          label="Create a new deck"
          icon={ICONS.Plus}
          onClick={() =>
            createDeck()
              .then(deck => deck && navigate(getDeckUrl(deck.uid)))
              .catch(showError)
          }
        />
        <MenuItem
          label="Create a new test"
          icon={ICONS.Plus}
          onClick={() =>
            createTest()
              .then(test => test && navigate(getTestQuestionsUrl(test.uid)))
              .catch(showError)
          }
        />
        {me?.isAdmin && (
          <MenuItem
            label="Admin page"
            icon={ICONS.Settings}
            onClick={() => navigate(getAdminUrl())}
          />
        )}
        <MenuItem
          label="Edit profile"
          icon={ICONS.Settings}
          disabled={!me}
          onClick={() => editUser(orDie(me).uid).catch(showError)}
        />
        <MenuHr />
        <LogoutMenuItem />
      </Menu>
    </div>
  );
};

export default memo(AppHeader);

const BackupMenuItem = () => {
  const { showMessage, showError } = useMessageModal();

  return (
    <MenuItem
      label="Export"
      icon={ICONS.Export}
      onClick={() => {
        showMessage(
          `Backup is creating. It can create several seconds. Please wait`
        );
        apiGetBackup()
          .then(data => saveAsFile(data, getBackupFileName()))
          .catch(showError);
      }}
    />
  );
};

const LogoutMenuItem = () => (
  <MenuItem
    label="Logout"
    icon={ICONS.Logout}
    onClick={() => apiLogout().then(() => location.replace('/'))}
  />
);

type InfoPanelProps = {
  dashboard: TDashboard | null;
};
const InfoPanel = ({ dashboard }: InfoPanelProps) => {
  return (
    <div className={css.title}>
      <em className={css.emTotal}>{dashboard?.total ?? '…'}</em>
      <Icon icon="Cards" className={css.headerCardsIcon} />
      {dashboard?.pending && dashboard.pending > 0 && (
        <>
          {' '}
          <em className={css.emPending}>+{dashboard.pending}</em>
        </>
      )}
    </div>
  );
};
